// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.RelationshipDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import m, { extendObservable } from 'mobx';
import dataModelDataFetcher from 'fetchers/Izenda.DataModelDataFetcher';
// eslint-disable-next-line max-len
import RelationshipDetailValidator from 'modules/Settings/DataSetup/DataModel/Relationship/Izenda.RelationshipDetailValidator';
import trackable from 'share/tracking/Izenda.Trackable';
import { JOIN_TYPE_CROSS, FIELDCHECK } from 'logic/data/Izenda.RelationshipConstants';
import objectState from 'logic/data/Izenda.ObjectStateConstants';
// $FlowFixMe
import { isEmpty, uniqueId, map, isNil, isArray, head, get, find } from 'lodash/fp';

function RelationshipDetail(item: any): void {
  item.querySources = [];
  item.querySourceFields = [];
  item.foreignQuerySources = [];
  item.foreignQuerySourceFields = [];
  item.generalEror = '';
  item.error = {};
  item.errors = item.errors || [];
  item.position = !isNil(item.position) ? parseInt(item.position) : uniqueId();
  item.positionId = item.positionId;
  item.editable = true;
  item.state = !isNil(item.state) ? item.state : objectState.INSERT;

  item.joinConnectionId = !String.isEmptyGuid(item.joinConnectionId) ? item.joinConnectionId : null;
  item.joinQuerySourceId = !String.isEmptyGuid(item.joinQuerySourceId) ? item.joinQuerySourceId : null;
  item.foreignConnectionId = !String.isEmptyGuid(item.foreignConnectionId) ? item.foreignConnectionId : null;
  item.foreignQuerySourceId = !String.isEmptyGuid(item.foreignQuerySourceId) ? item.foreignQuerySourceId : null;
  this.validator = new RelationshipDetailValidator();

  extendObservable(trackable(this), item);
  this.InitData(item);
}

RelationshipDetail.prototype.InitData = m.action(function(item: any): void {
  if (!isEmpty(item.joinConnectionId)) {
    this.reloadQuerySources(item.joinConnectionId).then(
      function(result) {
        this.querySources = result;
      }.bind(this)
    );
  }

  if (!isEmpty(item.joinQuerySourceId)) {
    this.reloadQuerySourceFields(item.joinQuerySourceId).then(
      function(result) {
        this.querySourceFields = result;
      }.bind(this)
    );
  }

  if (!isEmpty(item.foreignConnectionId)) {
    this.reloadQuerySources(item.foreignConnectionId).then(
      function(result) {
        this.foreignQuerySources = result;
      }.bind(this)
    );
  }

  if (!isEmpty(item.foreignConnectionId)) {
    this.reloadQuerySourceFields(item.foreignQuerySourceId).then(
      function(result) {
        this.foreignQuerySourceFields = result;
      }.bind(this)
    );
  }
});

RelationshipDetail.prototype.getQuerySourceOptions = function(querySources: any): void {
  return map(function(n) {
    return {
      key: n.id,
      value: n.name,
      valueDisplay: n.category + '.' + n.name
    };
  }, querySources);
};

RelationshipDetail.prototype.getListQuerySource = function() {
  return this.getQuerySourceOptions(this.querySources);
};

RelationshipDetail.prototype.getListForeignQuerySource = function() {
  return this.getQuerySourceOptions(this.foreignQuerySources);
};

RelationshipDetail.prototype.getListJoinField = function() {
  return this.querySourceFields;
};

RelationshipDetail.prototype.getListForeignJoinField = function() {
  return this.foreignQuerySourceFields;
};

RelationshipDetail.prototype.setDatabase = m.action(function(id: string): void {
  this.joinConnectionId = id;
  this.setQuerySource(null);
  isEmpty(this.joinConnectionId)
    ? (this.querySources = [])
    : this.reloadQuerySources(this.joinConnectionId).then(
        function(result) {
          this.querySources = result;
        }.bind(this)
      );
  this.updateState();
});

RelationshipDetail.prototype.setQuerySource = m.action(function(id: string): void {
  this.joinQuerySourceId = id;
  this.joinFieldId = null;
  isEmpty(this.joinQuerySourceId)
    ? (this.querySourceFields = [])
    : this.reloadQuerySourceFields(this.joinQuerySourceId).then(
        function(result) {
          this.querySourceFields = result;
        }.bind(this)
    );
  this.joinQuerySourceName = get('name', find(i => i.id === id, this.querySources));
  this.updateState();
  this.markDirty();
});

RelationshipDetail.prototype.setJoinType = m.action(function(id: string): void {
  if (this.joinType === id) {
    return;
  }
  this.joinType = id;
  this.updateState();
  this.markDirty();
});

/** 
 * Set PositionId for a Relationship
 * @param {number} id
 */ 
RelationshipDetail.prototype.setPositionId = m.action(function(id:number): void {
  this.positionId = id;
  this.updateState();
  this.markDirty();
});

RelationshipDetail.prototype.getJoinType = function() {
  return this.joinType;
};

RelationshipDetail.prototype.isCrossType = function() {
  return this.joinType === JOIN_TYPE_CROSS;
};

RelationshipDetail.prototype.getDatabase = function() {
  return this.joinConnectionId;
};

RelationshipDetail.prototype.getQuerySource = function() {
  return this.joinQuerySourceId;
};

RelationshipDetail.prototype.setJoinField = m.action(function(id: string): void {
  this.joinFieldId = id;
  this.updateState();
  this.markDirty();
});

RelationshipDetail.prototype.getJoinField = function() {
  return this.joinFieldId;
};

RelationshipDetail.prototype.setForeignDatabase = m.action(function(id: string): void {
  this.foreignConnectionId = id;
  this.setForeignQuerySource(null);
  isEmpty(this.foreignConnectionId)
    ? (this.foreignQuerySources = [])
    : this.reloadQuerySources(this.foreignConnectionId).then(
        function(result) {
          this.foreignQuerySources = result;
        }.bind(this)
      );
  this.updateState();
  this.markDirty();
});

RelationshipDetail.prototype.setForeignQuerySource = m.action(function(id: string): void {
  this.foreignQuerySourceId = id;
  this.foreignFieldId = null;
  isEmpty(this.foreignQuerySourceId)
    ? (this.foreignQuerySourceFields = [])
    : this.reloadQuerySourceFields(this.foreignQuerySourceId).then(
        function(result) {
          this.foreignQuerySourceFields = result;
        }.bind(this)
    );
  this.foreignQuerySourceName = get('name', find(i => i.id === id, this.foreignQuerySources));
  this.updateState();
  this.markDirty();
});

RelationshipDetail.prototype.getForeignDatabase = function() {
  return this.foreignConnectionId;
};

RelationshipDetail.prototype.getForeignQuerySource = function() {
  return this.foreignQuerySourceId;
};

RelationshipDetail.prototype.setForeignField = m.action(function(id: string): void {
  this.foreignFieldId = id;
  this.updateState();
  this.markDirty();
});

RelationshipDetail.prototype.getForeignField = function() {
  return this.foreignFieldId;
};

/*
* if relationship is system then return true
*/
RelationshipDetail.prototype.isDisableJoinType = function() {
  const disabled = this.systemRelationship === true;
  return disabled;
};

/*
* if relationship is system then return true
*/
RelationshipDetail.prototype.isDisableDatabase = function() {
  const disabled = this.systemRelationship === true;
  return disabled;
};

/*
* if relationship is system then return true
*/
RelationshipDetail.prototype.isDisableForeignDatabase = function() {
  const disabled = this.systemRelationship === true;
  return disabled;
};

/*
* if connection database is empty or relationship is system then return true
*/
RelationshipDetail.prototype.isDisableDataObject = function() {
  const database = this.getDatabase();
  const disabled = isEmpty(database) || this.systemRelationship === true;
  return disabled;
};

/*
* if foreign connection database is empty or relationship is system then return true
*/
RelationshipDetail.prototype.isDisableForeignDataObject = function() {
  const database = this.getForeignDatabase();
  const disabled = isEmpty(database) || this.systemRelationship === true;
  return disabled;
};

/*
* if QuerySource is empty or relationship is system or joinType is Cross then return true
*/
RelationshipDetail.prototype.isDisableJoinField = function() {
  const querySource = this.getQuerySource();
  const disabled = isEmpty(querySource) || this.systemRelationship === true || this.joinType === JOIN_TYPE_CROSS;
  return disabled;
};

/*
* if Foreign QuerySource is empty or relationship is system or joinType is Cross then return true
*/
RelationshipDetail.prototype.isDisableForeignField = function() {
  const querySource = this.getForeignQuerySource();
  const disabled = isEmpty(querySource) || this.systemRelationship === true || this.joinType === JOIN_TYPE_CROSS;
  return disabled;
};

/*
* field: is field of relationship item
*/
RelationshipDetail.prototype.isDisableField = function(field: string): boolean {
  let disabled = false;
  switch (field) {
    case FIELDCHECK.JOIN_QUERYSOURCE_FIELD:
      disabled = this.isDisableDataObject();
      break;
    case FIELDCHECK.JOIN_FIELD:
      disabled = this.isDisableJoinField();
      break;
    case FIELDCHECK.FOREIGN_QUERYSOURCE_FIELD:
      disabled = this.isDisableForeignDataObject();
      break;
    case FIELDCHECK.FOREIGN_FIELD:
      disabled = this.isDisableForeignField();
      break;
    case FIELDCHECK.JOIN_TYPE:
      disabled = this.isDisableJoinType();
      break;
    case FIELDCHECK.DATABASE_FIELD:
      disabled = this.isDisableDatabase();
      break;
    case FIELDCHECK.FOREIGN_DATABASE_FIELD:
      disabled = this.isDisableForeignDatabase();
      break;
  }
  return disabled;
};

/**
 * set editable for a relationship
 * @param {boolean} isEditable
 */
RelationshipDetail.prototype.setEditable = function(isEditable: boolean): boolean {
  this.editable = isEditable;
};

/**
 * reload Query Source base on the connection id
 */
RelationshipDetail.prototype.reloadQuerySources = function(joinConnectionId: string): void {
  const assign = function(result) {
    return result || {};
  }.bind(this);

  return dataModelDataFetcher.getQuerySourcesInfo(joinConnectionId).then(assign);
};

/**
 * Get data for Join Field dropdow list
 */
RelationshipDetail.prototype.reloadQuerySourceFields = function(joinQuerySourceId: string): void {
  const assign = function(result) {
    return result || {};
  }.bind(this);

  return dataModelDataFetcher.getBasicQuerySourceFieldsInfo(joinQuerySourceId).then(assign);
};

/**
 * Validate detail of relationship
 */
RelationshipDetail.prototype.validate = function() {
  this.error = this.validator.validate(this, this.isCrossType());
  return isEmpty(this.error);
};

/**
 * Update state
 */
RelationshipDetail.prototype.updateState = function() {
  // just update state to UPDATE when editing existing item
  this.id && this.setState(objectState.UPDATE);
};

/**
 * Set state
 */
RelationshipDetail.prototype.setState = function(state: any): void {
  this.state = state;
};

export default RelationshipDetail;


// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.RelationshipDetail.js